import { ref } from "vue";
import { defineStore } from "pinia";

const BANNER_KEY = "homepage_banners";
const BANNER_COLLECTION = "banners";
const BANNER_MOBILE_COLLECTION = "banners_mobile";

export interface HomepageBanner {
  id: string;
  name: string;
  url: string;
  imageUrl: string;
  mobileImageUrl: string;
}

interface AttachmentLike {
  id: string;
  [key: string]: unknown;
}

interface SettingMetadata {
  banners?: { attachmentId: string; mobileAttachmentId?: string | null; name?: string; url?: string }[];
  [key: string]: unknown;
}

interface PublicSetting {
  key: string;
  metadata?: SettingMetadata | string | null;
  attachments?: Record<string, AttachmentLike[]>;
}

export const useSettingsStore = defineStore("settings", () => {
  const banners = ref<HomepageBanner[]>([]);
  const loaded = ref(false);
  let bannerRequest: Promise<void> | null = null;

  // Resolved here, in the store's setup, NOT inside `mapBanners`.
  //
  // `useImageUrl` calls `useRuntimeConfig()`, and `mapBanners` runs after
  // `fetchBanners` has awaited its `$fetch`. On the server that is outside the
  // Nuxt context, so the call threw, the catch below swallowed it, and the page
  // rendered the premium fallback with no slider. It only ever worked because
  // `fetchBanners` used to run exclusively client-side, where a composable after
  // an await still resolves.
  const { attachmentUrl } = useImageUrl();

  // JSON columns can arrive as an object (auto-parsed) or a string.
  function parseMetadata(setting: PublicSetting): SettingMetadata {
    const meta = setting.metadata;
    if (!meta) return {};
    if (typeof meta === "string") {
      try {
        return JSON.parse(meta) as SettingMetadata;
      } catch {
        return {};
      }
    }
    return meta;
  }

  // Metadata carries the name/url/order and the desktop↔mobile pairing;
  // attachments carry the images. A banner shows only when its metadata entry
  // still has a live desktop attachment. The mobile image is optional and
  // resolved from the banners_mobile collection by the paired id.
  function mapBanners(settings: PublicSetting[]): HomepageBanner[] {
    const setting = settings.find((s) => s.key === BANNER_KEY);
    if (!setting) return [];

    const attachments = setting.attachments?.[BANNER_COLLECTION] || [];
    const mobileAttachments = setting.attachments?.[BANNER_MOBILE_COLLECTION] || [];
    const meta = parseMetadata(setting);
    const metaBanners = Array.isArray(meta.banners) ? meta.banners : [];

    return metaBanners
      .map((entry) => {
        const attachment = attachments.find((a) => a.id === entry.attachmentId);
        if (!attachment) return null;
        const mobile = entry.mobileAttachmentId
          ? mobileAttachments.find((m) => m.id === entry.mobileAttachmentId)
          : undefined;
        return {
          id: entry.attachmentId,
          name: entry.name || "",
          url: entry.url || "",
          // Use the original upload, not the "banner" variant — that variant is
          // a 1600×400 cover-crop (4:1) that mangles the 7:1 desktop strip and
          // 2:1 mobile image. CSS aspect-ratio frames them correctly.
          imageUrl: attachmentUrl(attachment, "original"),
          mobileImageUrl: mobile ? attachmentUrl(mobile, "original") : "",
        };
      })
      .filter((b): b is HomepageBanner => b !== null);
  }

  /**
   * Concurrent callers share one request. `/search` loads banners alongside its
   * catalogue so the strip is present in the server-rendered HTML, while
   * `HomepageBanners` keeps its own `onMounted` fetch as a safety net — without
   * sharing, both fire the same call.
   */
  async function fetchBanners() {
    if (bannerRequest) return bannerRequest;

    const config = useRuntimeConfig();

    bannerRequest = (async () => {
      try {
        const response = await $fetch<PublicSetting[]>(
          `${config.public.apiBase}/api/public/settings/images`,
        );
        banners.value = mapBanners(response);
        // Only a success sets `loaded`. It is what suppresses the client-side
        // retry in `HomepageBanners`' `onMounted`, so setting it in a `finally`
        // meant one failed server-side call permanently pinned the page to the
        // premium fallback — the real banners could never arrive.
        loaded.value = true;
      } catch {
        banners.value = [];
      } finally {
        bannerRequest = null;
      }
    })();

    return bannerRequest;
  }

  return {
    banners,
    loaded,
    fetchBanners,
    mapBanners,
  };
});
